// libs
import React, { memo, useEffect, useState, useCallback, useRef } from 'react';
import ErrorWrapper from 'shared/layouts/Listing/ErrorWrapperV2';
import { useHistory } from 'react-router';

// components
import Api from 'shared/layouts/ImageSearch/Api/Api';
import SimilarProducts from './SimilarProducts';
import ImageV2 from 'shared/components/ImageV2';
import MobileImageCapture from 'shared/components/ImageSearch/components/Mobile';

// styles
import { LoadingIndicator } from './SimilarProducts/styles';

import {
  PillContainer,
  ImageThumbnail,
  PillContent,
  CameraIconWrapper,
  CameraIconStyled,
  RetryButton,
} from './styles';

// helpers
import { getDeviceType, DEVICE_TYPE } from 'shared/helpers/app';

const Container = () => {
  const MobileImageCaptureComponent = MobileImageCapture as React.ComponentType<any>;
  const [imageToProcess, setImageToProcess] = useState('');
  const [imageKey, setImageKey] = useState('');
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  // const [categories, setCategories] = useState([]);
  // const [accessories, setAccessories] = useState([]);
  const [showImageCapture, setShowImageCapture] = useState(false);
  const history = useHistory();
  const isMobile = getDeviceType() === DEVICE_TYPE.MOBILE;
  const lastDataRef = useRef(null);

  // Load image once on mount
  useEffect(() => {
    const storedImage = localStorage.getItem('imageToProcess');
    if (storedImage) {
      setImageToProcess(storedImage);
      setImageKey(storedImage.substring(0, 50)); // Use part of the image as key
    } else if (window.location.pathname === '/image-search') {
      // If no image and directly accessing the page, redirect to home
      history.replace('/');
    }

    // Check for selected item in URL
    const urlParams = new URLSearchParams(window.location.search);
    const selectedItemParam = urlParams.get('selectedItem');
    if (selectedItemParam) {
      setSelectedItem(selectedItemParam);
    }
  }, [history]);

  // Extract categories and generate accessory suggestions from the API data
  const processProductData = useCallback((data) => {
    if (!data || !Array.isArray(data) || data.length === 0) return;
    setLoading(false);

    // Extract categories from the products
    const allCategories = data.flatMap((product) => product.categoryId || []);
    const uniqueCategories = [...new Set(allCategories)];

    // Map category IDs to readable names
    const categoryMap = {
      '2': 'Men',
      '102': 'Apparel',
      '6823': 'Western Wear',
      '6824': 'Shirts',
      '6826': 'Casual Shirts',
      '6834': 'Bottom Wear',
      '6835': 'Jeans',
      '6841': 'Ethnic Wear',
      '6842': 'Kurtas',
      '7204': 'Denim',
      '7741': 'Kurta Sets',
    };

    // Get readable category names
    const readableCategories = uniqueCategories.map((id) => categoryMap[id]).filter(Boolean);

    // Generate accessory suggestions based on detected categories
    const accessorySuggestions = [];

    if (readableCategories.includes('Kurtas') || readableCategories.includes('Ethnic Wear')) {
      accessorySuggestions.push('Mojaris', 'Ethnic Watches', 'Cufflinks');
    }

    if (readableCategories.includes('Shirts') || readableCategories.includes('Western Wear')) {
      accessorySuggestions.push('Ties', 'Cufflinks', 'Watches');
    }

    if (readableCategories.includes('Jeans') || readableCategories.includes('Bottom Wear')) {
      accessorySuggestions.push('Belts', 'Shoes', 'Socks');
    }

    // Add some general accessories if we don't have specific ones
    if (accessorySuggestions.length === 0) {
      if (readableCategories.includes('Men')) {
        accessorySuggestions.push('Watches', 'Wallets', 'Belts', 'Sunglasses');
      } else {
        accessorySuggestions.push('Jewelry', 'Bags', 'Scarves', 'Sunglasses');
      }
    }

    // Comment out these lines since the state variables are commented out
    // setCategories(readableCategories);
    // setAccessories([...new Set(accessorySuggestions.flat())]);
  }, []);

  // Process data when it changes
  useEffect(() => {
    if (apiData) {
      processProductData(apiData);
    }
  }, [apiData, processProductData]);

  // Handler to update apiData from Api render prop
  const handleApiData = useCallback((data) => {
    // Only update if data actually changes
    if (lastDataRef.current !== data) {
      lastDataRef.current = data;
      setApiData(data);
    }
  }, []);

  const onRetakeImage = useCallback(() => {
    setShowImageCapture(true);
  }, []);

  // Handle image capture completion
  const handleImageCapture = useCallback((imageSrc) => {
    if (imageSrc) {
      setImageToProcess(imageSrc);
      setImageKey(imageSrc.substring(0, 50));
      localStorage.setItem('imageToProcess', imageSrc);
    }
    setShowImageCapture(false);
  }, []);

  return (
    <ErrorWrapper>
      {imageToProcess && isMobile && (
        <PillContainer>
          <ImageThumbnail>
            <ImageV2 src={imageToProcess} width={54} height={54} alt="thumbnail" />
            <CameraIconWrapper>
              <CameraIconStyled />
            </CameraIconWrapper>
          </ImageThumbnail>
          <PillContent>
            <RetryButton onClick={onRetakeImage}>Retry</RetryButton>
          </PillContent>
        </PillContainer>
      )}
      {showImageCapture && (
        <MobileImageCaptureComponent
          onClose={() => setShowImageCapture(false)}
          onCapture={handleImageCapture}
        />
      )}
      {loading && <LoadingIndicator>Loading products...</LoadingIndicator>}
      <Api imageKey={imageKey}>
        {(data, loadMore, loading) => {
          handleApiData(data);
          return (
            <>
              {data && (
                <SimilarProducts
                  data={data}
                  loadMore={loadMore}
                  loading={loading}
                  selectedItem={selectedItem}
                />
              )}
            </>
          );
        }}
      </Api>
    </ErrorWrapper>
  );
};

export default memo(Container);
